import { mergeRegister } from '@lexical/utils';
import { createCallback } from '@solid-primitives/rootless';
import { cn } from '@ui';
import { createEffect, For, useContext } from 'solid-js';
import { Portal } from 'solid-js/web';
import { LexicalWrapperContext } from '../../context/LexicalWrapperContext';
import {
  autoRegister,
  lazyRegister,
  registerEditorWidthObserver,
  registerInternalLayoutShiftListener,
} from '../shared/utils';
import type { NodekeyOffset } from './findAndReplacePlugin';
import {
  type FloatingStyle,
  getFloatingSearchHighlightPosition,
} from './getFloatingSearchHighlightStyle';
import { getSearchHighlightClientRects } from './searchHighlightTarget';

function registerEventListener<K extends keyof HTMLElementEventMap>(
  target: HTMLElement | null,
  type: K,
  listener: (event: HTMLElementEventMap[K]) => void
): () => void;
function registerEventListener(
  target: HTMLElement | null,
  type: string,
  listener: EventListener
) {
  if (!target) return () => {};
  target.addEventListener(type, listener);
  return () => target.removeEventListener(type, listener);
}

type SearchHighlightItem = {
  style: FloatingStyle;
  idx: number | undefined;
};

export function SearchHighlight(props: {
  anchorElem?: HTMLElement;
  listOffset: NodekeyOffset[];
  onStylesChange: (styles: SearchHighlightItem[]) => void;
  onMatchesChange: (matches: number) => void;
}): null {
  const lexicalWrapper = useContext(LexicalWrapperContext);
  const editor = () => lexicalWrapper?.editor;
  const anchorElem = () => props.anchorElem ?? document.body;
  let stateListOffsetRef: NodekeyOffset[] = [];
  let animationFrame: number | undefined;

  const updateTextFormatFloatingToolbar = createCallback(
    (listOffset: NodekeyOffset[]) => {
      const newStyles: { style: FloatingStyle; idx: number | undefined }[] = [];
      const matches = listOffset.reduce(
        (max, offset) => Math.max(max, offset.pairKey ?? 0),
        0
      );
      listOffset.map((offset: NodekeyOffset) => {
        const editorInstance = editor();
        if (!editorInstance) return;
        const element = editorInstance.getElementByKey(offset.key);
        if (!element) return;
        try {
          const rects = getSearchHighlightClientRects(element, offset.offset);
          [...rects].map((rect) => {
            const newStyle = getFloatingSearchHighlightPosition(
              rect,
              anchorElem()
            );
            const styleWidth = newStyle.width;
            if (
              Number.parseInt(
                styleWidth.substring(0, styleWidth.length - 2)
              ) !== 4
            ) {
              newStyles.push({ style: newStyle, idx: offset.pairKey });
            }
          });
        } catch (error) {
          console.error(error);
        }
      });

      props.onStylesChange(newStyles);
      props.onMatchesChange(matches);
    }
  );

  const update = createCallback(() => {
    if (animationFrame !== undefined) {
      cancelAnimationFrame(animationFrame);
    }

    animationFrame = requestAnimationFrame(() => {
      animationFrame = undefined;
      const editorInstance = editor();
      if (!editorInstance) return;

      editorInstance.getEditorState().read(() => {
        updateTextFormatFloatingToolbar(stateListOffsetRef);
      });
    });
  });

  createEffect(() => {
    stateListOffsetRef = props.listOffset;
    update();
  });

  lazyRegister(editor, (editorInstance) =>
    mergeRegister(
      registerInternalLayoutShiftListener(editorInstance, update),
      registerEditorWidthObserver(
        editorInstance,
        update,
        '[data-block-content]'
      ),
      editorInstance.registerUpdateListener(
        ({ dirtyElements, dirtyLeaves }) => {
          if (dirtyElements.size === 0 && dirtyLeaves.size === 0) return;
          update();
        }
      )
    )
  );

  autoRegister(
    registerEventListener(anchorElem().parentElement, 'scroll', update),
    () => {
      if (animationFrame !== undefined) {
        cancelAnimationFrame(animationFrame);
      }
    }
  );

  return null;
}

export function FloatingSearchHighlight(props: {
  anchorElem?: HTMLElement;
  styles: SearchHighlightItem[];
  currentMatch: number;
}) {
  return (
    <Portal mount={props.anchorElem}>
      <For each={props.styles}>
        {(item) => (
          <div
            style={item.style}
            class={cn(
              'z-10 m-0 text-transparent h-4.5 absolute top-0 left-0 opacity-50 pointer-events-none',
              item.idx === props.currentMatch + 1 ? 'bg-accent' : 'bg-accent/50'
            )}
          />
        )}
      </For>
    </Portal>
  );
}
